// ✅ src/pages/VehicleV2/JobCardEntry.jsx  (UPDATED FULL FILE)
// Backend expected:
//   app.use("/api/jobCardEntry", jobCardEntry);
//
// APIs used:
//   GET  https://nissi-india.com/api/jobCardEntry/init?loginUserId=&branchId=&branchType=&vehicleId=
//   GET  https://nissi-india.com/api/jobCardEntry/list?loginUserId=&branchId=&branchType=&vehicleId=&page=1&limit=10
//   POST https://nissi-india.com/api/jobCardEntry/save
//
// ✅ Updated pattern like BatteryTesting / latest screens:
// - common localStorage/sessionStorage header pattern
// - inventory header support
// - useMemo + useCallback pattern
// - loading + pageLoading states
// - vehicleId from query/localStorage
// - same UI structure style
// - safe mapping for init/list response
// - submit sends dd/MM/yyyy entryDate

import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaArrowLeft, FaSave, FaUndo } from "react-icons/fa";
import { useNavigate, useLocation } from "react-router-dom";

const API_BASE = "https://nissi-india.com/api/jobCardEntry";

const toText = (v, def = "") => {
  if (v === null || v === undefined) return def;
  return String(v);
};

const toStr = (v, def = "") => {
  if (v === null || v === undefined) return def;
  return String(v).trim();
};

const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

const clean = (v) => String(v ?? "").trim();

const pad2 = (n) => String(n).padStart(2, "0");

const formatDDMMYYYY = (dateObj) => {
  if (!(dateObj instanceof Date) || Number.isNaN(dateObj.getTime())) return "";
  return `${pad2(dateObj.getDate())}/${pad2(
    dateObj.getMonth() + 1
  )}/${dateObj.getFullYear()}`;
};

const ymdToDDMMYYYY = (ymd) => {
  if (!ymd) return "";
  const [y, m, d] = String(ymd).split("-");
  if (!y || !m || !d) return "";
  return `${pad2(d)}/${pad2(m)}/${y}`;
};

const parseDDMMYYYY = (s) => {
  const parts = String(s || "").split("/");
  if (parts.length !== 3) return null;

  const [dd, mm, yyyy] = parts;
  const d = Number(dd);
  const m = Number(mm);
  const y = Number(yyyy);

  if (!Number.isFinite(d) || !Number.isFinite(m) || !Number.isFinite(y)) {
    return null;
  }

  const dt = new Date(y, m - 1, d);
  if (
    dt.getFullYear() !== y ||
    dt.getMonth() !== m - 1 ||
    dt.getDate() !== d
  ) {
    return null;
  }

  return dt;
};

const parseAnyDateToYMD = (raw) => {
  const val = clean(raw);
  if (!val) return "";

  const ddmmyyyy = parseDDMMYYYY(val);
  if (ddmmyyyy) {
    return `${ddmmyyyy.getFullYear()}-${pad2(ddmmyyyy.getMonth() + 1)}-${pad2(
      ddmmyyyy.getDate()
    )}`;
  }

  const dt = new Date(val);
  if (!Number.isNaN(dt.getTime())) {
    return `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}-${pad2(
      dt.getDate()
    )}`;
  }

  return "";
};

const JobCardEntry = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  // ✅ shared localStorage / sessionStorage pattern
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId =
    localStorage.getItem("branchId") ||
    localStorage.getItem("branchid") ||
    "8";
  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "HO";
  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "";
  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  // ✅ get vehicleId from query/localStorage
  const vehicleId = useMemo(() => {
    const sp = new URLSearchParams(location?.search || "");
    const fromQuery = toInt(sp.get("vehicleId") || sp.get("vehicleid"));
    if (fromQuery) return fromQuery;

    return toInt(localStorage.getItem("vehicleId") || 0);
  }, [location]);

  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [headerInfo, setHeaderInfo] = useState({
    jobCardNo: "",
    vehicleName: "",
    vehicleRegNo: "",
    vehicleCode: "",
    location: "",
  });

  const [form, setForm] = useState({
    entryDateYMD: "",
    hrKm: "",
    status: "",
    natureOfComplaint: "",
    complaintAttendedBy: "",
  });

  const [rows, setRows] = useState([]);

  // pagination
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);

  const totalPages = useMemo(() => {
    const t = Math.ceil(total / limit);
    return t > 0 ? t : 1;
  }, [total, limit]);

  // ---- styles ----
  const inputClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    isDark
      ? "bg-gray-700/30 border-gray-600 text-white"
      : "bg-white border-gray-300 text-gray-900"
  }`;

  const labelClass = `block text-sm font-medium mb-2 ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  const cardClass = `p-6 mt-6 rounded-2xl shadow-xl border ${
    isDark
      ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
      : `bg-white ${currentSkin.glass} text-black`
  }`;

  const mapHeader = useCallback((data) => {
    const h = data?.header || data?.vehicle || data || {};

    return {
      jobCardNo: toText(h?.jobCardNo ?? h?.fld_jobcardno),
      vehicleName: toText(h?.vehicleName ?? h?.fld_vehicle_name),
      vehicleRegNo: toText(h?.vehicleRegNo ?? h?.fld_vehicle_regno),
      vehicleCode: toText(h?.vehicleCode ?? h?.fld_vehicle_code),
      location: toText(h?.location ?? h?.fld_Worklocationname),
    };
  }, []);

  const mapEdit = useCallback((edit) => {
    const raw = edit || {};

    return {
      entryDateYMD: parseAnyDateToYMD(
        raw?.entryDate ?? raw?.entryDateYMD ?? raw?.fld_entrydate1 ?? raw?.fld_entrydate
      ),
      hrKm: toText(raw?.hrKm ?? raw?.fld_hrmtrreading),
      status: toText(raw?.status ?? raw?.fld_status),
      natureOfComplaint: toText(
        raw?.natureOfComplaint ?? raw?.fld_natureofcomplaint
      ),
      complaintAttendedBy: toText(
        raw?.complaintAttendedBy ?? raw?.fld_complaintattendedby
      ),
    };
  }, []);

  const mapRow = useCallback((r) => {
    return {
      id: toInt(r?.id ?? r?.jobCardId ?? r?.fld_jobcardid ?? 0),
      jobCardNo: toText(r?.jobCardNo ?? r?.fld_jobcardno),
      entryDate: toText(
        r?.entryDate ??
          r?.fld_entrydate1 ??
          r?.fld_entrydate
      ),
      vehicleCode: toText(r?.vehicleCode ?? r?.fld_vehicle_code),
      vehicleName: toText(r?.vehicleName ?? r?.fld_vehicle_name),
      vehicleRegNo: toText(r?.vehicleRegNo ?? r?.fld_vehicle_regno),
      location: toText(r?.location ?? r?.fld_Worklocationname),
      hrKm: toText(r?.hrKm ?? r?.fld_hrmtrreading),
      status: toText(r?.status ?? r?.fld_status),
      natureOfComplaint: toText(
        r?.natureOfComplaint ?? r?.fld_natureofcomplaint
      ),
      complaintAttendedBy: toText(
        r?.complaintAttendedBy ?? r?.fld_complaintattendedby
      ),
      _raw: r,
    };
  }, []);

  /* ------------------------- INIT LOAD ------------------------- */
  const loadInit = useCallback(async () => {
    try {
      setPageLoading(true);

      const { data } = await axios.get(`${API_BASE}/init`, {
        params: {
          loginUserId: toInt(loginUserId, 0),
          branchId: toInt(branchId, 0),
          branchType: clean(branchType),
          vehicleId: toInt(vehicleId, 0),
        },
        headers: commonHeaders,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Init failed");
      }

      setHeaderInfo(mapHeader(data));

      if (data?.edit) {
        setForm((prev) => ({
          ...prev,
          ...mapEdit(data.edit),
        }));
      }

      if (Array.isArray(data?.grid?.list)) {
        const list = data.grid.list.map(mapRow);
        setRows(list);
        setTotal(toInt(data?.grid?.total, 0));
        setPage(toInt(data?.grid?.page, 1));
        setLimit(toInt(data?.grid?.limit, limit));
      }
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || err?.message || "Init failed"
      );
    } finally {
      setPageLoading(false);
    }
  }, [
    loginUserId,
    branchId,
    branchType,
    vehicleId,
    commonHeaders,
    mapHeader,
    mapEdit,
    mapRow,
    limit,
  ]);

  /* ------------------------- LIST LOAD ------------------------- */
  const loadList = useCallback(
    async (nextPage = page, nextLimit = limit) => {
      try {
        setLoading(true);

        const { data } = await axios.get(`${API_BASE}/list`, {
          params: {
            loginUserId: toInt(loginUserId, 0),
            branchId: toInt(branchId, 0),
            branchType: clean(branchType),
            vehicleId: toInt(vehicleId, 0),
            page: toInt(nextPage, 1),
            limit: toInt(nextLimit, 10),
          },
          headers: commonHeaders,
        });

        if (!data?.success) {
          throw new Error(data?.message || "Failed to load list");
        }

        const list = Array.isArray(data?.list) ? data.list.map(mapRow) : [];
        setRows(list);
        setTotal(toInt(data?.total, 0));
        setPage(toInt(data?.page, nextPage));
        setLimit(toInt(data?.limit, nextLimit));
      } catch (err) {
        console.error(err);
        toast.error(
          err?.response?.data?.message || err?.message || "List failed"
        );
        setRows([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    },
    [
      page,
      limit,
      loginUserId,
      branchId,
      branchType,
      vehicleId,
      commonHeaders,
      mapRow,
    ]
  );

  /* ------------------------- VALIDATION ------------------------- */
  const validate = () => {
    if (!form.entryDateYMD) {
      toast.warn("Please select Entry Date");
      return false;
    }

    const entryDDMM = ymdToDDMMYYYY(form.entryDateYMD);
    const entryDt = parseDDMMYYYY(entryDDMM);

    if (!entryDt) {
      toast.warn("Invalid Entry Date");
      return false;
    }

    const today = new Date();
    const todayDateOnly = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    );
    const entryDateOnly = new Date(
      entryDt.getFullYear(),
      entryDt.getMonth(),
      entryDt.getDate()
    );

    if (entryDateOnly.getTime() > todayDateOnly.getTime()) {
      toast.warn("Please select Date less than or equal to current date.");
      return false;
    }

    if (!clean(form.hrKm)) {
      toast.warn("Hour/Kilo Meter Reading is required");
      return false;
    }

    if (!clean(form.status)) {
      toast.warn("Status is required");
      return false;
    }

    if (!clean(form.natureOfComplaint)) {
      toast.warn("Nature of Complaint is required");
      return false;
    }

    if (!clean(form.complaintAttendedBy)) {
      toast.warn("Complaint Attended by is required");
      return false;
    }

    return true;
  };

  /* ------------------------- ACTIONS ------------------------- */
  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      setSaving(true);

      const body = {
        loginUserId: toInt(loginUserId, 0),
        branchId: toInt(branchId, 0),
        branchType: clean(branchType),
        vehicleId: toInt(vehicleId, 0),
        entryDate: ymdToDDMMYYYY(form.entryDateYMD),
        hrKm: clean(form.hrKm),
        status: clean(form.status),
        natureOfComplaint: clean(form.natureOfComplaint),
        complaintAttendedBy: clean(form.complaintAttendedBy),
      };

      const { data } = await axios.post(`${API_BASE}/save`, body, {
        headers: commonHeaders,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Save failed");
      }

      toast.success(data?.message || "Saved successfully");
      handleReset();
      await loadList(1, limit);
      setPage(1);
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || err?.message || "Save failed"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setForm({
      entryDateYMD: "",
      hrKm: "",
      status: "",
      natureOfComplaint: "",
      complaintAttendedBy: "",
    });
  };

  const handleBack = () => {
    navigate(-1);
  };

  /* ------------------------- PAGINATION ------------------------- */
  const canPrev = page > 1;
  const canNext = page < totalPages;

  const paginationText = useMemo(() => {
    const from = total === 0 ? 0 : (page - 1) * limit + 1;
    const to = Math.min(total, page * limit);
    return `${from}-${to} of ${total}`;
  }, [page, limit, total]);

  /* ------------------------- EFFECTS ------------------------- */
  useEffect(() => {
    loadInit();
  }, [loadInit]);

  useEffect(() => {
    loadList(page, limit);
  }, [page, limit, loadList]);

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">JOB CARD ENTRY</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div className={cardClass}>
          {/* Top header row */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <button
                onClick={handleBack}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
                disabled={loading || pageLoading || saving}
              >
                <FaArrowLeft />
                Back
              </button>

              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Job Card Entry
              </h1>
            </div>

            <div className={`text-sm ${isDark ? "text-gray-200" : "text-gray-700"}`}>
              INFRA SCREEN : B-29/1
            </div>
          </div>

          {/* Form area */}
          <div
            className={`rounded-xl p-6 mb-6 border ${
              isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
            }`}
          >
            {/* Row 1 */}
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
              <div className="md:col-span-2">
                <label className={labelClass}>Job Card No</label>
              </div>
              <div className="md:col-span-4">
                <div className={`${isDark ? "text-white" : "text-gray-900"} font-medium`}>
                  {headerInfo.jobCardNo || "-"}
                </div>
              </div>
              <div className="md:col-span-2" />
              <div className="md:col-span-2">
                <label className={labelClass}>
                  Hour/Kilo Meter Reading <span className="text-red-500">*</span>
                </label>
              </div>
              <div className="md:col-span-2">
                <input
                  type="text"
                  value={form.hrKm}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, hrKm: e.target.value }))
                  }
                  className={inputClass}
                  disabled={saving || loading || pageLoading}
                />
              </div>
            </div>

            <div className="h-5" />

            {/* Row 2 */}
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
              <div className="md:col-span-2">
                <label className={labelClass}>
                  Entry Date <span className="text-red-500">*</span>
                </label>
              </div>
              <div className="md:col-span-4">
                <input
                  type="date"
                  value={form.entryDateYMD}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, entryDateYMD: e.target.value }))
                  }
                  className={inputClass}
                  disabled={saving || loading || pageLoading}
                />
              </div>
              <div className="md:col-span-2" />
              <div className="md:col-span-2">
                <label className={labelClass}>
                  Status <span className="text-red-500">*</span>
                </label>
              </div>
              <div className="md:col-span-2">
                <input
                  type="text"
                  value={form.status}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, status: e.target.value }))
                  }
                  className={inputClass}
                  disabled={saving || loading || pageLoading}
                />
              </div>
            </div>

            <div className="h-5" />

            {/* Row 3 */}
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
              <div className="md:col-span-2">
                <label className={labelClass}>Vehicle Name</label>
              </div>
              <div className="md:col-span-4">
                <div className={`${isDark ? "text-white" : "text-gray-900"} font-medium`}>
                  {headerInfo.vehicleName || "-"}
                </div>
              </div>
              <div className="md:col-span-2" />
              <div className="md:col-span-2">
                <label className={labelClass}>
                  Nature of Complaint <span className="text-red-500">*</span>
                </label>
              </div>
              <div className="md:col-span-2">
                <input
                  type="text"
                  value={form.natureOfComplaint}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      natureOfComplaint: e.target.value,
                    }))
                  }
                  className={inputClass}
                  disabled={saving || loading || pageLoading}
                />
              </div>
            </div>

            <div className="h-5" />

            {/* Row 4 */}
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
              <div className="md:col-span-2">
                <label className={labelClass}>Vehicle Reg No</label>
              </div>
              <div className="md:col-span-4">
                <div className={`${isDark ? "text-white" : "text-gray-900"} font-medium`}>
                  {headerInfo.vehicleRegNo || "-"}
                </div>
              </div>
              <div className="md:col-span-2" />
              <div className="md:col-span-2">
                <label className={labelClass}>
                  Complaint Attended by <span className="text-red-500">*</span>
                </label>
              </div>
              <div className="md:col-span-2">
                <input
                  type="text"
                  value={form.complaintAttendedBy}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      complaintAttendedBy: e.target.value,
                    }))
                  }
                  className={inputClass}
                  disabled={saving || loading || pageLoading}
                />
              </div>
            </div>

            <div className="h-5" />

            {/* Row 5 */}
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
              <div className="md:col-span-2">
                <label className={labelClass}>Vehicle Code</label>
              </div>
              <div className="md:col-span-4">
                <div className={`${isDark ? "text-white" : "text-gray-900"} font-medium`}>
                  {headerInfo.vehicleCode || "-"}
                </div>
              </div>
              <div className="md:col-span-2" />
              <div className="md:col-span-2">
                <label className={labelClass}>Location</label>
              </div>
              <div className="md:col-span-2">
                <div className={`${isDark ? "text-white" : "text-gray-900"} font-medium`}>
                  {headerInfo.location || "-"}
                </div>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex justify-center gap-4 mt-8">
              <button
                onClick={handleSubmit}
                disabled={saving || loading || pageLoading}
                className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  saving || loading || pageLoading
                    ? "opacity-70 cursor-not-allowed"
                    : ""
                }`}
              >
                <FaSave />
                {saving ? "Saving..." : "Submit"}
              </button>

              <button
                onClick={handleReset}
                type="button"
                disabled={saving || loading || pageLoading}
                className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow border transition-all duration-200 ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-700"
                    : "border-gray-300 text-gray-800 hover:bg-gray-100"
                } ${
                  saving || loading || pageLoading
                    ? "opacity-70 cursor-not-allowed"
                    : ""
                }`}
              >
                <FaUndo />
                Reset
              </button>
            </div>
          </div>

          {/* Grid/table */}
          <div
            className={`rounded-xl shadow-sm border overflow-hidden ${
              isDark ? "border-gray-700" : "border-gray-200"
            }`}
          >
            <div className="overflow-x-auto">
              <table className="min-w-[1600px] w-full">
                <thead className={`${buttonClass} text-white`}>
                  <tr>
                    <th className="px-4 py-4 text-center font-semibold">S.No</th>
                    <th className="px-4 py-4 text-center font-semibold">JobCardNo</th>
                    <th className="px-4 py-4 text-center font-semibold">Entry Date</th>
                    <th className="px-4 py-4 text-center font-semibold">Vehicle code</th>
                    <th className="px-4 py-4 text-center font-semibold">Vehicle Name</th>
                    <th className="px-4 py-4 text-center font-semibold">Vehicle RegNo</th>
                    <th className="px-4 py-4 text-center font-semibold">Location</th>
                    <th className="px-4 py-4 text-center font-semibold">Hr/Km</th>
                    <th className="px-4 py-4 text-center font-semibold">Status</th>
                    <th className="px-4 py-4 text-center font-semibold">Nature Of Complaint</th>
                    <th className="px-4 py-4 text-center font-semibold">Complaint AttendedBy</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading || pageLoading ? (
                    <tr>
                      <td colSpan={11} className="text-center py-10">
                        Loading...
                      </td>
                    </tr>
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={11} className="text-center py-10">
                        No records found
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, idx) => (
                      <tr
                        key={r.id || idx}
                        className={`transition-colors duration-200 ${
                          isDark ? "bg-gray-900" : "bg-white"
                        }`}
                      >
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {(page - 1) * limit + idx + 1}
                        </td>

                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.jobCardNo}
                        </td>

                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.entryDate}
                        </td>

                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.vehicleCode}
                        </td>

                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.vehicleName}
                        </td>

                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.vehicleRegNo}
                        </td>

                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.location}
                        </td>

                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.hrKm}
                        </td>

                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.status}
                        </td>

                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.natureOfComplaint}
                        </td>

                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.complaintAttendedBy}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination bar */}
            <div
              className={`flex flex-col md:flex-row md:items-center md:justify-between gap-3 px-4 py-3 border-t ${
                isDark ? "border-gray-700 bg-gray-900" : "border-gray-200 bg-white"
              }`}
            >
              <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                {paginationText}
              </div>

              <div className="flex items-center gap-2">
                <select
                  value={limit}
                  onChange={(e) => {
                    setLimit(Number(e.target.value));
                    setPage(1);
                  }}
                  disabled={loading || pageLoading || saving}
                  className={inputClass}
                  style={{ width: 120 }}
                >
                  {[10, 20, 50, 100].map((n) => (
                    <option key={n} value={n}>
                      {n} / page
                    </option>
                  ))}
                </select>

                <button
                  disabled={!canPrev || loading || pageLoading || saving}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className={`px-4 py-2 rounded-lg border ${
                    canPrev && !loading && !pageLoading && !saving
                      ? "hover:bg-gray-50 dark:hover:bg-gray-800"
                      : "opacity-50 cursor-not-allowed"
                  } ${
                    isDark ? "border-gray-700 text-gray-200" : "border-gray-300 text-gray-700"
                  }`}
                >
                  Prev
                </button>

                <div className={`text-sm px-2 ${isDark ? "text-gray-200" : "text-gray-700"}`}>
                  Page {page} / {totalPages}
                </div>

                <button
                  disabled={!canNext || loading || pageLoading || saving}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  className={`px-4 py-2 rounded-lg border ${
                    canNext && !loading && !pageLoading && !saving
                      ? "hover:bg-gray-50 dark:hover:bg-gray-800"
                      : "opacity-50 cursor-not-allowed"
                  } ${
                    isDark ? "border-gray-700 text-gray-200" : "border-gray-300 text-gray-700"
                  }`}
                >
                  Next
                </button>
              </div>
            </div>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default JobCardEntry;